<template>
  <!--LAYBY LANDING PAGE: Story CD-19 -->
  <Card
    class="w-full max-w-[510px] min-h-[545px] overflow-hidden shadow-md rounded-xl h-full"
    :class="`${cardClasses} ${isMobileOrIsTable ? 'min-h-[343px]' : ''}`"
  >
    <CardContent class="p-0 flex flex-col h-full" :class="contentClasses">
      <div class="p-6 flex-1">
        <div
          class="mb-4 items-start"
          :class="{
            'flex justify-between items-center': isMobileOrIsTable || (isSuspended && !hasCancellationRequest),
          }"
        >
          <div>
            <p class="text-[16px] font-semibold" :class="titleClasses">{{ order?.merchantName }}</p>
            <p class="text-[14px] font-small" :class="titleClasses">{{ order?.reference }}</p>
          </div>
          <div
            v-if="isSuspended && !hasCancellationRequest"
            class="bg-warning-50 text-warning-700 border-warning-200 border-[1px] h-[28px] rounded-[9999px] flex justify-center items-center px-3 text-[12px] font-medium"
          >
            Suspended
          </div>
        </div>

        <div class="h-[40px]">
          <div class="flex justify-between mb-4" v-if="!isCompleted">
            <p class="text-[14px] text-grey-light-600">Target</p>
            <p class="text-[24px] font-semibold text-brand-700">
              {{ displayCurrency(orderTotal) }}
            </p>
          </div>
        </div>

        <div class="relative mb-3">
          <img
            :src="order?.imageUrl || DefaultLayupLogo"
            alt="Product"
            class="w-full rounded-[16px] object-contain"
            :class="isMobileOrIsTable ? 'h-[150px]' : 'h-[229px]'"
          />
        </div>

        <div v-if="!isCompleted">
          <div class="mb-3 flex items-end justify-between">
            <div>
              <p class="text-[14px] text-grey-light-600">Contributed</p>
              <p class="text-[20px] font-semibold text-brand-700">
                {{ displayCurrency(paidAmountInCents) }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-[14px] text-grey-light-600">Outstanding</p>
              <p class="text-[20px] font-semibold text-brand-700">
                {{ displayCurrency(orderBalance) }}
              </p>
            </div>
          </div>

          <div class="mb-3">
            <div class="flex gap-0 mb-0">
              <div class="relative h-2 w-full overflow-hidden rounded-full bg-grey-light-200">
                <div class="h-full transition-all" :class="progressBarClasses" :style="{ width: `${progress}%` }" />
              </div>
            </div>

            <div class="mt-2 flex items-center justify-between text-xs">
              <span class="text-[14px] text-grey-light-600"> {{ remainingMonths }}</span>
              <span v-if="!isCancelled && !isSuspended" class="font-medium text-[14px] text-grey-light-700"
                >{{ progress }}%</span
              >
            </div>
          </div>
        </div>
      </div>

      <div class="mt-auto">
        <hr :class="isCompleted ? 'border-brand-100' : ''" />
        <div class="flex gap-2 px-6 py-4 h-[73px]" :class="isCompleted ? 'justify-center' : 'justify-between'">
          <Button
            v-if="!isCompleted"
            variant="outline"
            size="sm"
            class="w-[66px] h-[40px] font-semibold"
            @click="navigateToDetails"
          >
            View
          </Button>

          <div
            v-if="isCompleted"
            size="sm"
            class="flex items-center justify-center gap-2 border-[1px] border-brand-300 text-brand-300 bg-brand-25 hover:bg-brand-25 w-[180px] h-[40px] rounded-lg"
          >
            <Check class="h-3 w-3" />
            Order Completed
          </div>

          <div
            v-else-if="isCancelled"
            size="sm"
            class="bg-error-50 text-error-700 hover:bg-error-50 min-w-[100px] border-error-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            Cancelled
          </div>

          <div
            v-else-if="isCancelling"
            size="sm"
            class="bg-error-50 text-error-700 hover:bg-error-50 min-w-[140px] border-error-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            Cancellation Requested
          </div>

          <div
            v-else-if="isSuspended && hasCancellationRequest"
            size="sm"
            class="bg-warning-50 text-warning-700 hover:bg-warning-50 min-w-[100px] border-warning-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            Suspended
          </div>

          <Button
            v-else-if="isSuspended"
            size="sm"
            class="bg-brand-600 hover:bg-brand-700 w-[132px] h-[40px] font-semibold"
            @click="showPaymentModal = true"
          >
            Make Payment
          </Button>

          <Button
            v-else
            size="sm"
            class="bg-brand-600 hover:bg-brand-700 w-[132px] h-[40px] font-semibold"
            @click="showPaymentModal = true"
          >
            Make Payment
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>

  <Dialog :open="showPaymentModal" @update:open="showPaymentModal = $event">
    <DialogContent class="max-w-[380px] rounded-xl p-3">
      <PaymentModal :order-id="order?._id || ''" />
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import DefaultLayupLogo from '@/assets/logo.svg';
import PaymentModal from '@/components/shopper/PaymentModal.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { displayCurrency } from '@/utils/format.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import dayjs from 'dayjs';
import { Check } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

interface Props {
  order?: ModelOrderResponse;
}

const props = defineProps<Props>();
const router = useRouter();
const showPaymentModal = ref(false);
const { isMobile, isTablet } = useBreakpoint();

const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);
const isCompleted = computed(() => props.order?.state === 'COMPLETED');
const isCancelled = computed(() => props.order?.state === 'CANCELLED');
const isCancelling = computed(() => props.order?.state === 'CANCELLING');
const isSuspended = computed(() => props.order?.state === 'SUSPENDED');
const hasCancellationRequest = computed(() => props.order?.orderCancellationRequest === 'ACCEPTED');

const orderBalance = computed(() => props.order?.balance ?? 0);
const orderTotal = computed(() => props.order?.amountDue ?? 0);

const cardClasses = computed(() => {
  if (isCompleted.value) {
    return 'border-[1px] border-brand-300 bg-brand-25';
  }

  if (isCancelled.value || (isSuspended.value && hasCancellationRequest.value)) {
    return 'border-[1px] border-grey-light-200 shadow-grey-light-200 opacity-50';
  }

  return 'border-[1px] border-grey-light-200 shadow-grey-light-200';
});

const contentClasses = computed(() => {
  return isCancelled.value || (isSuspended.value && hasCancellationRequest.value) ? 'opacity-80' : '';
});

const titleClasses = computed(() => {
  if (isCompleted.value) {
    return 'text-brand-500';
  }

  return 'text-grey-light-900';
});

const progressBarClasses = computed(() => {
  if (isCompleted.value) {
    return 'bg-brand-600';
  }

  if (isCancelled.value || isSuspended.value) {
    return 'bg-grey-light-300';
  }

  return 'bg-brand-600';
});

const progress = computed(() => {
  if (!orderTotal.value) return 0;

  return Math.round(((orderTotal.value - orderBalance.value) / orderTotal.value) * 100);
});

const paidAmountInCents = computed(() => {
  if (orderTotal.value === 0) return 0;

  return orderTotal.value - orderBalance.value;
});

const remainingMonths = computed(() => {
  if (paidAmountInCents.value === orderTotal.value) {
    return 0;
  }

  if (!props.order?.plans || props.order.plans.length === 0) {
    return '0 months remaining';
  }

  const plan = props.order.plans[0];
  const frequency = plan.frequency === 'WEEKLY' ? 'week' : 'month';
  const timeToGo = props.order.endDateMax ? -dayjs().diff(props.order.endDateMax, frequency) : 0;
  const quantity = plan.quantity || 0;

  const remaining = timeToGo < quantity ? timeToGo : quantity;
  const label = frequency === 'week' ? 'weeks' : 'months';

  return `${Math.max(0, remaining)} ${label} remaining`;
});

const navigateToDetails = () => {
  router.push({ name: 'shopperLaybyDetails', params: { id: props.order?._id } });
};
</script>
